import React from 'react';
import { Layers, AlertCircle } from 'lucide-react';
import type { QuotaSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './QuotaGroup.module.css';

interface Props {
  data: QuotaSection;
}

function usagePct(used: number, limit: number) {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function fillClass(pct: number) {
  if (pct >= 95) return shared['progress-fill--red'];
  if (pct >= 80) return shared['progress-fill--amber'];
  return shared['progress-fill--green'];
}

function valueClass(pct: number) {
  if (pct >= 95) return shared['stat-value--red'];
  if (pct >= 80) return shared['stat-value--amber'];
  return '';
}

function ProgressRow({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = usagePct(used, limit);
  return (
    <div className={styles['progress-row']}>
      <div className={styles['progress-row-header']}>
        <span className={styles['progress-row-label']}>{label}</span>
        <span className={`${shared['stat-value']} ${valueClass(pct)} ${styles['progress-row-count']}`}>
          {used} / {limit}
        </span>
      </div>
      <div className={shared['progress-track']} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className={`${shared['progress-fill']} ${fillClass(pct)}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function QuotaGroup({ data }: Props) {
  const eventsPct = usagePct(data.events.used, data.events.limit);
  const hasWarning = data.eventDetails.some((e) => {
    const pcts = [
      usagePct(e.photos.used, e.photos.limit),
      usagePct(e.albums.used, e.albums.limit),
      usagePct(e.ftpAccounts.used, e.ftpAccounts.limit),
    ];
    return pcts.some((p) => p >= 80);
  });

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><Layers size={15} /></div>
        <div>
          <div className={shared['group-title']}>
            Quota &amp; Headroom
            {hasWarning && <AlertCircle size={13} className={styles['warn-icon']} aria-label="Quota warning" />}
          </div>
          <div className={shared['group-subtitle']}>How much runway is left before limits are hit</div>
        </div>
      </div>

      {/* Events gauge */}
      <div className={styles['events-gauge-row']}>
        <div className={styles['events-gauge']}>
          <svg viewBox="0 0 48 48" className={styles['gauge-svg']} aria-hidden="true">
            <circle cx="24" cy="24" r="20" fill="none" stroke="var(--border-subtle)" strokeWidth="5" />
            <circle
              cx="24" cy="24" r="20"
              fill="none"
              stroke={eventsPct >= 95 ? 'var(--clay-orange)' : eventsPct >= 80 ? '#f59e0b' : '#22c55e'}
              strokeWidth="5"
              strokeDasharray={`${(eventsPct / 100) * 125.6} 125.6`}
              strokeLinecap="round"
              transform="rotate(-90 24 24)"
            />
          </svg>
          <div className={styles['gauge-label']}>
            <span className={styles['gauge-num']}>{data.events.used}</span>
            <span className={styles['gauge-denom']}>/ {data.events.limit}</span>
          </div>
        </div>
        <div>
          <div className={styles['events-label']}>Events used</div>
          <div className={styles['events-sub']}>
            {data.events.limit - data.events.used} slot{data.events.limit - data.events.used !== 1 ? 's' : ''} remaining
          </div>
        </div>
      </div>

      {/* Per-event details */}
      {data.eventDetails.length > 0 && (
        <div className={styles['event-list']}>
          <div className={shared['section-label']}>Per-event usage</div>
          {data.eventDetails.map((e) => (
            <div key={e.eventId} className={styles['event-row']}>
              <div className={styles['event-name']}>{e.eventName}</div>
              <div className={styles['event-bars']}>
                <ProgressRow label="Photos" used={e.photos.used} limit={e.photos.limit} />
                <ProgressRow label="Albums" used={e.albums.used} limit={e.albums.limit} />
                <ProgressRow label="FTP" used={e.ftpAccounts.used} limit={e.ftpAccounts.limit} />
                <ProgressRow label="Live" used={e.liveAlbums.used} limit={e.liveAlbums.limit} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending requests */}
      {data.pendingRequests.length > 0 && (
        <div className={styles['pending-row']}>
          <AlertCircle size={13} className={styles['pending-icon']} />
          <span className={styles['pending-text']}>
            {data.pendingRequests.length} quota increase request{data.pendingRequests.length > 1 ? 's' : ''} pending review
          </span>
        </div>
      )}
    </div>
  );
}
